'use client';

import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  saveConsent,
  getConsent,
  logConsentAction,
  type ConsentRegion,
  type ConsentRecord,
} from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  isOpen: boolean;
  onClose: () => void;
  region: ConsentRegion;
}

export default function ConsentPreferenceCenter({
  isOpen,
  onClose,
  region,
}: ConsentPreferenceCenterProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [choices, setChoices] = useState<ConsentRecord['choices']>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  // Load current consent on open
  useEffect(() => {
    if (isOpen) {
      const existing = getConsent();
      if (existing) {
        setChoices(existing.choices);
      }
    }
  }, [isOpen]);

  // Focus trap and keyboard handling
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const focusableElements = dialogRef.current.querySelectorAll(
      'button, [role="switch"], a[href]'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen, onClose]);

  const handleToggle = (category: keyof ConsentRecord['choices']) => {
    if (category === 'necessary') return; // Necessary cannot be toggled
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    saveConsent(choices, region);
    logConsentAction('preferences_saved', { choices, region });
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setChoices(allAccepted);
    saveConsent(allAccepted, region);
    logConsentAction('accept_all_from_preferences', { region });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 id="preferences-title" className="text-lg font-semibold text-gray-900">
            Cookie Preferences
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close preferences"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Necessary */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-medium text-gray-900">Necessary Cookies</label>
              <button
                role="switch"
                aria-checked
                disabled
                className="w-12 h-6 bg-blue-600 rounded-full relative cursor-not-allowed"
              >
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Essential for site functionality, security, and authentication. Always enabled.
            </p>
          </div>

          {/* Analytics */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-medium text-gray-900">Analytics Cookies</label>
              <button
                role="switch"
                aria-checked={choices.analytics}
                onClick={() => handleToggle('analytics')}
                className={`w-12 h-6 rounded-full relative transition-colors ${
                  choices.analytics ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    choices.analytics ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Help us understand how you use our site to improve your experience.
            </p>
          </div>

          {/* Marketing */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-medium text-gray-900">Marketing Cookies</label>
              <button
                role="switch"
                aria-checked={choices.marketing}
                onClick={() => handleToggle('marketing')}
                className={`w-12 h-6 rounded-full relative transition-colors ${
                  choices.marketing ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    choices.marketing ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Enable personalized ads and content based on your interests.
            </p>
          </div>

          {/* Preferences */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-medium text-gray-900">Preference Cookies</label>
              <button
                role="switch"
                aria-checked={choices.preferences}
                onClick={() => handleToggle('preferences')}
                className={`w-12 h-6 rounded-full relative transition-colors ${
                  choices.preferences ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    choices.preferences ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Remember your preferences and settings for a personalized experience.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 font-medium rounded-lg transition-colors"
          >
            Save Preferences
          </button>
          <button
            onClick={handleAcceptAll}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
